import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { InboxNotification } from '../api/notificationApi';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead
} from '../api/notificationApi';

export const useInboxStore = defineStore('inbox', () => {
  const items = ref<InboxNotification[]>([]);
  const unreadCount = ref(0);
  const loading = ref(false);
  const menuOpen = ref(false);
  /** SSE·시드 등으로 방금 도착한 알림 — NEW 딱지 표시용 */
  const newArrivalIds = ref<Set<number>>(new Set());
  const hasNewArrival = ref(false);

  async function loadList() {
    loading.value = true;
    try {
      const data = await fetchNotifications(30, false);
      items.value = data.items;
      unreadCount.value = data.unreadCount;
    } finally {
      loading.value = false;
    }
  }

  async function refreshUnreadCount() {
    unreadCount.value = await fetchUnreadCount();
  }

  function markNewArrival(notificationId: number) {
    if (!notificationId) return;
    newArrivalIds.value = new Set(newArrivalIds.value).add(notificationId);
    hasNewArrival.value = true;
  }

  function prependNotification(notification: InboxNotification) {
    const id = notification.notificationId;
    if (id && items.value.some((i) => i.notificationId === id)) return;
    items.value = [notification, ...items.value].slice(0, 30);
    if (id) markNewArrival(id);
  }

  function showNewBadge(item: InboxNotification): boolean {
    if (!isUnread(item)) return false;
    const id = item.notificationId;
    if (id && newArrivalIds.value.has(id)) return true;
    return isUnread(item);
  }

  function setUnreadCount(count: number) {
    unreadCount.value = Math.max(0, count);
  }

  function isUnread(item: InboxNotification): boolean {
    return String(item.isRead ?? 'N').trim().toUpperCase() !== 'Y';
  }

  function clearNewArrival(notificationId?: number) {
    if (notificationId != null) {
      const next = new Set(newArrivalIds.value);
      next.delete(notificationId);
      newArrivalIds.value = next;
    } else {
      newArrivalIds.value = new Set();
    }
    hasNewArrival.value = items.value.some((i) => isUnread(i) && newArrivalIds.value.has(i.notificationId));
  }

  async function markRead(notificationId: number) {
    const count = await markNotificationRead(notificationId);
    unreadCount.value = count;
    const row = items.value.find((i) => i.notificationId === notificationId);
    if (row) row.isRead = 'Y';
    clearNewArrival(notificationId);
    if (unreadCount.value === 0) hasNewArrival.value = false;
  }

  async function markAllRead() {
    unreadCount.value = await markAllNotificationsRead();
    items.value.forEach((i) => {
      i.isRead = 'Y';
    });
    clearNewArrival();
    hasNewArrival.value = false;
  }

  function reset() {
    items.value = [];
    unreadCount.value = 0;
    loading.value = false;
    menuOpen.value = false;
    newArrivalIds.value = new Set();
    hasNewArrival.value = false;
  }

  return {
    items,
    unreadCount,
    loading,
    menuOpen,
    hasNewArrival,
    newArrivalIds,
    loadList,
    refreshUnreadCount,
    prependNotification,
    markNewArrival,
    showNewBadge,
    isUnread,
    setUnreadCount,
    markRead,
    markAllRead,
    reset
  };
});
